import {
  AsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';

import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { Report } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  Tooth,
  ToothFullHeadMPR,
} from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import {
  DefaultInitialState,
  LoadingStateType,
  SliceName,
} from '@/shared/config';
import { UltimateMedicalImageMode } from '@/shared/graphics/RenderComponents/Presets/UltimateMedicalImage';

import {
  FMXNavigationMode,
  MaskStyleTypes,
  type ToothMPRSliceOptionsUpdate,
} from '../config/reports.type';

import {
  archiveReport,
  deleteReport,
  deleteReport_DEPRECATED,
  markToothAsDeleted,
  markToothDetectionAsDeleted,
  resetReportTooth,
  setToothNumerationAndRevive,
  setToothDetectionNumerationAndRevive,
  setToothFullHeadMPRSliceAnnotations,
  setToothFullHeadMPRSliceEditorState,
  setToothFullHeadMPRSliceSelection,
  setToothFullHeadMPRSliceViewOptions,
  setToothMPRSliceOptions,
  restoreReport,
  updateTeethState,
} from './reportSlice.thunks';

type ReportInitialState = DefaultInitialState & {
  fullHeadMprByToothID: Record<string, ToothFullHeadMPR>;
  fullHeadMprDeletedToothIDs: string[];
  fullHeadMprSnapshot: {
    reportID: string;
    requestID: string;
    loading: LoadingStateType;
    initialRevisions: Record<string, number>;
    excludedToothIDs: string[];
  };
  fullHeadMprSelectionPendingByToothID: Record<string, number>;
  fullHeadMprEditorPendingByToothID: Record<string, number>;
  fullHeadMprSliceOptionsPendingByToothID: Record<
    string,
    ToothMPRSliceOptionsUpdate[]
  >;
  flags: {
    isReportRequestPending: boolean;
    isDeleteReportPending: boolean;
    isReportROIPending: boolean;
  };
  maskStyleType: MaskStyleTypes;
  showMasks: boolean;
  showPeriapicalRadiolucencyMasks: boolean;
  toothChartHeight: number;
  navigationMode: FMXNavigationMode;
  activeSubImageIndex: number;
  activeToothID: string;
  activeSubImagesIDs: string[];
  activePageNumber: number;
  disabledSubImagesIDs: string[];
  subImagesIDs: string[];
  ioxRayReportViewOptions: MedicalImageViewOptions;
  toolbarActiveControl: string;
  toolsMode: UltimateMedicalImageMode;
  lastUpdatedCropID: string;
  currentReport: Report | undefined;
  focusedMetaImageID: string;
  dataStream: {
    reportID: string;
    loading: LoadingStateType;
  };
  hoveredMaskID?: string;
};

const initialState: ReportInitialState = {
  fullHeadMprByToothID: {},
  fullHeadMprDeletedToothIDs: [],
  fullHeadMprSnapshot: {
    reportID: '',
    requestID: '',
    loading: 'idle',
    initialRevisions: {},
    excludedToothIDs: [],
  },
  fullHeadMprSelectionPendingByToothID: {},
  fullHeadMprEditorPendingByToothID: {},
  fullHeadMprSliceOptionsPendingByToothID: {},
  flags: {
    isReportRequestPending: false,
    isDeleteReportPending: false,
    isReportROIPending: false,
  },
  loading: 'idle',
  maskStyleType: MaskStyleTypes.Default,
  showMasks: true,
  showPeriapicalRadiolucencyMasks: true,
  toothChartHeight: 0,
  navigationMode: FMXNavigationMode.MatrixView,
  activeSubImageIndex: 0,
  activePageNumber: 0,
  activeToothID: '',
  activeSubImagesIDs: [],
  disabledSubImagesIDs: [],
  currentReport: undefined,
  subImagesIDs: [],
  ioxRayReportViewOptions: {} as MedicalImageViewOptions,
  toolbarActiveControl: 'view',
  toolsMode: 'view',
  lastUpdatedCropID: '',
  focusedMetaImageID: '',
  dataStream: {
    reportID: '',
    loading: 'idle',
  },
  hoveredMaskID: '',
};

const sortReports = (a: Report, b: Report) => {
  const aDate = a.Created?.At?.toDate().getTime() ?? 0;
  const bDate = b.Created?.At?.toDate().getTime() ?? 0;

  return bDate - aDate;
};

export const reportsAdapter = createEntityAdapter<Report>({
  selectId: (report) => report.ID,
  sortComparer: sortReports,
});

const reportState = reportsAdapter.getInitialState(initialState);

type ReportState = typeof reportState;

const setNewestFullHeadMpr = (state: ReportState, mpr: ToothFullHeadMPR) => {
  if (
    mpr.ReportID !== state.fullHeadMprSnapshot.reportID ||
    state.fullHeadMprDeletedToothIDs.includes(mpr.ID)
  ) {
    return;
  }
  const current = state.fullHeadMprByToothID[mpr.ID];
  if (
    !current ||
    (mpr.Revision?.Number ?? 0) > (current.Revision?.Number ?? 0)
  ) {
    state.fullHeadMprByToothID[mpr.ID] = mpr;
  }
};

const syncFullHeadMprDeletedTeeth = (state: ReportState, teeth: Tooth[]) => {
  teeth.forEach((tooth) => {
    if (tooth.ReportID !== state.fullHeadMprSnapshot.reportID) {
      return;
    }
    state.fullHeadMprDeletedToothIDs = state.fullHeadMprDeletedToothIDs.filter(
      (id) => id !== tooth.ID,
    );
    if (tooth.Deleted?.Deleted) {
      delete state.fullHeadMprByToothID[tooth.ID];
      state.fullHeadMprDeletedToothIDs.push(tooth.ID);
      if (!state.fullHeadMprSnapshot.excludedToothIDs.includes(tooth.ID)) {
        state.fullHeadMprSnapshot.excludedToothIDs.push(tooth.ID);
      }
    }
  });
};

const setNewestOne = (state: ReportState, action: PayloadAction<Report>) => {
  const currentRevisionNumber =
    state.entities[action.payload.ID]?.Revision?.Number ?? 0;
  const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

  if (payloadRevisionNumber > currentRevisionNumber) {
    reportsAdapter.setOne(state, action.payload);

    if (state.currentReport?.ID === action.payload.ID) {
      state.currentReport = action.payload;
    }
  }
};

type StateFlags = keyof typeof initialState.flags;

const reportsSlice = createSlice({
  name: SliceName.reports,
  initialState: reportState,
  reducers: {
    startFullHeadMprSnapshot: (
      state,
      { payload }: PayloadAction<{ reportID: string; requestID: string }>,
    ) => {
      if (state.fullHeadMprSnapshot.reportID !== payload.reportID) {
        state.fullHeadMprDeletedToothIDs = [];
      }
      state.fullHeadMprByToothID = Object.fromEntries(
        Object.entries(state.fullHeadMprByToothID).filter(
          ([, mpr]) => mpr.ReportID === payload.reportID,
        ),
      );
      state.fullHeadMprSnapshot = {
        ...payload,
        loading: 'pending',
        excludedToothIDs: [],
        initialRevisions: Object.fromEntries(
          Object.entries(state.fullHeadMprByToothID).map(([id, mpr]) => [
            id,
            mpr.Revision?.Number ?? 0,
          ]),
        ),
      };
    },
    completeFullHeadMprSnapshot: (
      state,
      {
        payload,
      }: PayloadAction<{ requestID: string; records: ToothFullHeadMPR[] }>,
    ) => {
      if (state.fullHeadMprSnapshot.requestID !== payload.requestID) {
        return;
      }
      const receivedIDs = payload.records.map((record) => record.ID);
      state.fullHeadMprByToothID = Object.fromEntries(
        Object.entries(state.fullHeadMprByToothID).filter(
          ([id, mpr]) =>
            receivedIDs.includes(id) ||
            (mpr.Revision?.Number ?? 0) !==
              state.fullHeadMprSnapshot.initialRevisions[id],
        ),
      );
      payload.records
        .filter(
          (mpr) => !state.fullHeadMprSnapshot.excludedToothIDs.includes(mpr.ID),
        )
        .forEach((mpr) => setNewestFullHeadMpr(state as ReportState, mpr));
      state.fullHeadMprSnapshot.loading = 'succeeded';
    },
    failFullHeadMprSnapshot: (state, { payload }: PayloadAction<string>) => {
      if (state.fullHeadMprSnapshot.requestID === payload) {
        state.fullHeadMprSnapshot.loading = 'failed';
      }
    },
    setNewestOne: (state, action: PayloadAction<Report>) => {
      setNewestOne(state as ReportState, action);
    },
    addOne: (state, action: PayloadAction<Report>) => {
      reportsAdapter.addOne(state as ReportState, action.payload);
    },
    addMany: (state, action: PayloadAction<Report[]>) => {
      reportsAdapter.addMany(state as ReportState, action.payload);
    },
    setOne: (state, action: PayloadAction<Report>) => {
      reportsAdapter.setOne(state as ReportState, action.payload);
    },
    removeAll: (state) => {
      reportsAdapter.removeAll(state as ReportState);
    },
    setMany: (state, action: PayloadAction<Report[]>) => {
      reportsAdapter.setMany(state as ReportState, action.payload);
    },
    removeOne: (state, action: PayloadAction<EntityId>) => {
      reportsAdapter.removeOne(state as ReportState, action.payload);
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    startDataStream: (state, action: PayloadAction<string>) => {
      state.dataStream = { reportID: action.payload, loading: 'pending' };
    },
    setDataStreamSucceeded: (state, action: PayloadAction<string>) => {
      if (state.dataStream.reportID === action.payload) {
        state.dataStream.loading = 'succeeded';
      }
    },
    setDataStreamFailed: (state, action: PayloadAction<string>) => {
      if (state.dataStream.reportID === action.payload) {
        state.dataStream.loading = 'failed';
      }
    },
    resetDataStream: (state) => {
      state.dataStream = { reportID: '', loading: 'idle' };
      state.fullHeadMprByToothID = {};
      state.fullHeadMprDeletedToothIDs = [];
      state.fullHeadMprSnapshot = initialState.fullHeadMprSnapshot;
    },
    setMaskStyleType: (state, action) => {
      state.maskStyleType = action.payload;
    },
    setShowMasks: (state, action) => {
      state.showMasks = action.payload;
    },
    setShowPeriapicalRadiolucencyMasks: (state, action) => {
      state.showPeriapicalRadiolucencyMasks = action.payload;
    },
    setToothChartHeight: (state, action) => {
      state.toothChartHeight = action.payload;
    },
    setNavigationMode: (state, action) => {
      state.navigationMode = action.payload;
    },
    setActiveSubImageIndex: (state, action) => {
      state.activeSubImageIndex = action.payload;
    },
    setActivePage: (state, action) => {
      state.activePageNumber = action.payload;
    },
    setActiveToothID: (state, action) => {
      state.activeToothID = action.payload;
    },
    setFocusedMetaImageID: (state, action: PayloadAction<string>) => {
      state.focusedMetaImageID = action.payload;
    },
    setHoveredMaskID: (state, action: PayloadAction<string | undefined>) => {
      state.hoveredMaskID = action.payload;
    },
    setActiveSubImagesIDs: (state, action: PayloadAction<string[]>) => {
      state.activeSubImagesIDs = action.payload;
    },
    setDisabledSubImagesIDs: (state, action: PayloadAction<string[]>) => {
      state.disabledSubImagesIDs = action.payload;
    },
    setSubImagesIDs: (state, action: PayloadAction<string[]>) => {
      state.subImagesIDs = action.payload;
    },
    setToolbarActiveControl: (state, action: PayloadAction<string>) => {
      state.toolbarActiveControl = action.payload;
    },
    setLastUpdatedCropID: (state, action: PayloadAction<string>) => {
      state.lastUpdatedCropID = action.payload;
    },
    setToolsMode: (state, action: PayloadAction<UltimateMedicalImageMode>) => {
      state.toolsMode = action.payload;
    },
    setIOXRayReportViewOptions: (
      state,
      action: PayloadAction<MedicalImageViewOptions>,
    ) => {
      state.ioxRayReportViewOptions = action.payload;
    },
    setCurrentReport: (state, action: PayloadAction<Report>) => {
      state.currentReport = action.payload;
    },
    updateCurrentReport: (state, action: PayloadAction<Report>) => {
      const currentRevisionNumber = state.currentReport?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (payloadRevisionNumber > currentRevisionNumber) {
        state.currentReport = action.payload;
      }
    },
    toggleNavigationMode: (state) => {
      if (state.navigationMode === FMXNavigationMode.MatrixView) {
        state.navigationMode = FMXNavigationMode.ToothChart;
      } else {
        state.navigationMode = FMXNavigationMode.MatrixView;
      }
      state.activeToothID = '';
      state.focusedMetaImageID = '';
      state.toolbarActiveControl = 'view'; // Set toolbar to init state
    },
    removeCurrentReport: (state) => {
      state.currentReport = undefined;
    },
    // TODO: [4|m] Need to make report settings object or even make separate redux slice for it.
    // It,s hard to maintain inside report slice.
    // Also need to review all this props, find deprecated and unused and remove them.
    resetReportViewSettings: (state) => {
      state.showMasks = true;
      state.toothChartHeight = 0;
      state.navigationMode = FMXNavigationMode.MatrixView;
      state.activeSubImageIndex = 0;
      state.activePageNumber = 0;
      state.activeToothID = '';
      state.activeSubImagesIDs = [];
      state.disabledSubImagesIDs = [];
      state.currentReport = undefined;
      state.subImagesIDs = [];
      state.ioxRayReportViewOptions = {} as MedicalImageViewOptions;
      state.toolbarActiveControl = 'view';
      state.toolsMode = 'view';
      state.lastUpdatedCropID = '';
      state.focusedMetaImageID = '';
      state.hoveredMaskID = '';
    },
    reset: () => reportsAdapter.getInitialState(initialState),
  },
  extraReducers: (builder) => {
    const asyncRequestCreator = <A, B, C>(
      // @ts-expect-error WARN: C doesn't satisfy AsyncThunkConfig, but this is not public type. Just ignore this or remove this function and use builder itself.
      thunkAction: AsyncThunk<A, B, C>,
      pendingFlagKey: StateFlags,
      fulfilledCallback?: (payload: unknown) => void,
    ) => {
      builder.addCase(thunkAction.pending, (state) => {
        state.flags[pendingFlagKey] = true;
      });
      builder.addCase(thunkAction.fulfilled, (state, { payload }) => {
        state.flags[pendingFlagKey] = false;
        if (typeof fulfilledCallback === 'function') {
          fulfilledCallback(payload);
        }
      });
      builder.addCase(thunkAction.rejected, (state) => {
        state.flags[pendingFlagKey] = false;
      });
    };

    asyncRequestCreator(deleteReport_DEPRECATED, 'isDeleteReportPending');

    builder.addMatcher(
      isAnyOf(archiveReport.fulfilled, restoreReport.fulfilled),
      (state, { payload }) => {
        if (payload.Report) {
          setNewestOne(state as ReportState, {
            type: 'reports/setNewestOne',
            payload: payload.Report,
          });
        }
      },
    );
    builder.addMatcher(
      isAnyOf(
        setToothMPRSliceOptions.pending,
        setToothMPRSliceOptions.fulfilled,
        setToothMPRSliceOptions.rejected,
      ),
      (state, action) => {
        const { ToothID: toothID, series, Action: update } = action.meta.arg;

        if (!toothID || !series || update?.case !== 'SetOptions') {
          return;
        }

        const options = update.value?.[series];
        const pending =
          state.fullHeadMprSliceOptionsPendingByToothID[toothID] ?? [];

        if (setToothMPRSliceOptions.pending.match(action)) {
          if (
            options?.ThicknessMm === undefined ||
            options.SliceStepMm === undefined
          ) {
            return;
          }

          state.fullHeadMprSliceOptionsPendingByToothID[toothID] = [
            ...pending,
            {
              requestID: action.meta.requestId,
              series,
              options: {
                ThicknessMm: options.ThicknessMm,
                SliceStepMm: options.SliceStepMm,
              },
            },
          ];
          return;
        }

        const remaining = pending.filter(
          (update) => update.requestID !== action.meta.requestId,
        );

        if (remaining.length) {
          state.fullHeadMprSliceOptionsPendingByToothID[toothID] = remaining;
        } else {
          delete state.fullHeadMprSliceOptionsPendingByToothID[toothID];
        }
      },
    );
    builder.addMatcher(
      isAnyOf(
        markToothAsDeleted.fulfilled,
        markToothDetectionAsDeleted.fulfilled,
        setToothNumerationAndRevive.fulfilled,
      ),
      (state, { payload }) => {
        syncFullHeadMprDeletedTeeth(
          state as ReportState,
          payload.Tooth ? [payload.Tooth] : [],
        );
      },
    );
    builder.addMatcher(
      isAnyOf(
        updateTeethState.fulfilled,
        setToothDetectionNumerationAndRevive.fulfilled,
        resetReportTooth.fulfilled,
      ),
      (state, { payload }) => {
        syncFullHeadMprDeletedTeeth(state as ReportState, payload.Teeth);
      },
    );
    builder.addMatcher(
      isAnyOf(deleteReport.fulfilled, deleteReport_DEPRECATED.fulfilled),
      (state, { payload }) => {
        if (state.fullHeadMprSnapshot.reportID === payload.DeletedReportID) {
          state.fullHeadMprSnapshot = initialState.fullHeadMprSnapshot;
          state.fullHeadMprDeletedToothIDs = [];
        }
        state.fullHeadMprByToothID = Object.fromEntries(
          Object.entries(state.fullHeadMprByToothID).filter(
            ([, mpr]) => mpr.ReportID !== payload.DeletedReportID,
          ),
        );
      },
    );
    (
      [
        [
          setToothFullHeadMPRSliceSelection,
          'fullHeadMprSelectionPendingByToothID',
        ],
        [
          setToothFullHeadMPRSliceEditorState,
          'fullHeadMprEditorPendingByToothID',
        ],
      ] as const
    ).forEach(([thunkAction, pendingKey]) => {
      builder.addMatcher(
        isAnyOf(
          thunkAction.pending,
          thunkAction.fulfilled,
          thunkAction.rejected,
        ),
        (state, action) => {
          const toothID = action.meta.arg.ToothID;
          if (toothID) {
            state[pendingKey][toothID] = Math.max(
              0,
              (state[pendingKey][toothID] ?? 0) +
                (thunkAction.pending.match(action) ? 1 : -1),
            );
          }
        },
      );
    });
    builder.addMatcher(
      isAnyOf(
        setToothMPRSliceOptions.fulfilled,
        setToothFullHeadMPRSliceSelection.fulfilled,
        setToothFullHeadMPRSliceAnnotations.fulfilled,
        setToothFullHeadMPRSliceViewOptions.fulfilled,
        setToothFullHeadMPRSliceEditorState.fulfilled,
        resetReportTooth.fulfilled,
      ),
      (state, { payload }) => {
        if (payload.ToothFullHeadMPR) {
          setNewestFullHeadMpr(state as ReportState, payload.ToothFullHeadMPR);
        }
      },
    );
  },
});

export const { actions } = reportsSlice;

export default reportsSlice.reducer;
